import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { Button } from "@/components/ui/button/Button";
import { CsvTable } from "./CsvTable";
import {
  getCsvTablePreview,
  removeCsvTablePreview,
} from "./csvTablePreviewStore";
import { downloadCsvTablePreviewFile } from "./downloadCsvTablePreviewFile";

export interface CsvTablePreviewWindowProps {
  contentId: string;
}

/** Full-height CSV preview surface for the ``csv-table-preview`` utility window. */
export function CsvTablePreviewWindow({ contentId }: CsvTablePreviewWindowProps) {
  const stored = useMemo(() => getCsvTablePreview(contentId), [contentId]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [contentId]);

  useEffect(() => {
    return () => {
      removeCsvTablePreview(contentId);
    };
  }, [contentId]);

  const file = stored?.files[selectedIndex] ?? stored?.files[0];

  if (!stored || !file) {
    return (
      <div
        className="dark flex h-full items-center justify-center bg-slate-950 p-6 text-sm text-slate-400"
        data-testid="csv-table-preview-missing"
      >
        Preview is no longer available in this session.
      </div>
    );
  }

  const dataRowCount = Math.max(0, file.rows.length - 1);
  const download = file.download;

  return (
    <div
      className="dark flex h-full min-h-0 flex-col overflow-hidden bg-slate-950 text-slate-100"
      data-testid="csv-table-preview-window"
    >
      {stored.files.length > 1 && (
        <div
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-white/10 px-3 py-2 sm:px-4"
          role="tablist"
          aria-label="Preview files"
          data-testid="csv-table-preview-file-tabs"
        >
          {stored.files.map((candidate, index) => (
            <button
              key={`${index}-${candidate.label ?? ""}`}
              type="button"
              role="tab"
              aria-selected={index === selectedIndex}
              onClick={() => setSelectedIndex(index)}
              className={clsx(
                "shrink-0 rounded px-2.5 py-1 text-xs font-medium transition-colors",
                index === selectedIndex
                  ? "bg-white/10 text-slate-100"
                  : "text-slate-400 hover:bg-white/5 hover:text-slate-200",
              )}
              data-testid={`csv-table-preview-file-tab-${index}`}
            >
              {candidate.label ?? `File ${index + 1}`}
            </button>
          ))}
        </div>
      )}
      <div className="min-h-0 flex-1 px-3 py-3 sm:px-4">
        <CsvTable rows={file.rows} className="h-full" />
      </div>
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-white/10 px-3 py-2 sm:px-4">
        <p className="min-w-0 truncate text-xs text-slate-500" data-testid="csv-table-preview-row-count">
          {dataRowCount} data row{dataRowCount !== 1 ? "s" : ""}
          {download ? <span className="font-mono"> · {download.filename}</span> : null}
        </p>
        {download && (
          <Button
            outline
            size="sm"
            icon={<FontAwesomeIcon icon={faDownload} />}
            onClick={() => downloadCsvTablePreviewFile(download)}
            data-testid="csv-table-preview-download"
            aria-label={`Download ${download.filename}`}
          >
            Download
          </Button>
        )}
      </div>
    </div>
  );
}
